/* eslint-disable max-len */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TimeFormatPipe } from 'app/core/pipes/timeFormat.pipe';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { DispatchBoardService } from '../dispatch-board/dispatch-board.service';
import { Subscription } from 'rxjs';
import { Moment } from 'moment';
import moment from 'moment';
import { endTimeMinValidator } from '../reschedule-job/reschedule-job.component';
import _ from 'lodash';

@Component({
  selector: 'app-reschedule-route',
  templateUrl: './reschedule-route.component.html',
  styleUrls: ['./reschedule-route.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RescheduleRouteComponent implements OnInit, OnDestroy {

  rescheduleRouteForm: UntypedFormGroup;
  startDateSubscription: Subscription;
  startTimePickerSubscription: Subscription;
  endTimePickerSubscription: Subscription;
  startDateTime: Moment;
  endDateTime: Moment;
  routeDuration: number;
  timeFormat: string;
  dateFormat: string = 'DD/MM/YYYY';
  disableUpdateBtn: boolean = false;
  dialogTitle: string;
  routeDetails: any;
  warningsList: any;
  userDetails: any;
  customUserDetails: any;
  blockJobsOnHoliday: any;
  blockHolidayName: any;
  isHoliday: any;
  showRescheduleSuccessToast: boolean = true;
  validatingOverlapsAndTimeoff: boolean = false;
  getWarningMsg = _.debounce((): void => {
    const [startDateTime, endDateTime] = this.getStartAndEndTime();
    this.warningsList = this._dispatchBoardService.getScheduleWarningMsg('route', this.routeDetails, this.userDetails, startDateTime, endDateTime, this.customUserDetails, true, false);
    [this.isHoliday, this.blockHolidayName, this.blockJobsOnHoliday] = this._dispatchBoardService.isConflictWithHolidays(startDateTime, endDateTime);
    this.disableUpdateBtn = this.blockJobsOnHoliday || this.warningsList?.routeDurationExceedWarning?.show;
  }, 350, {leading: true, trailing: true});

  constructor(
    private _localisationService: LocalisationService,
    public _utilityService: UtilityService,
    private timeFormatPipe: TimeFormatPipe,
    private formBuilder: UntypedFormBuilder,
    private _dispatchBoardService: DispatchBoardService,
    private _dataCollectorService: DataCollectorService,
    private changeDetectorRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<RescheduleRouteComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) { }

  ngOnInit(): void {
    const browserTimezone: string = moment.tz.guess();
    this._localisationService.setDefaultLanguage();
    this.dateFormat = this._utilityService.dateFormat;
    this.timeFormat = this._utilityService.timeFormat;
    this.startDateTime = moment.utc(this.dialogData.data?.startDateTime).tz(browserTimezone);
    this.endDateTime = moment.utc(this.dialogData.data?.endDateTime).tz(browserTimezone);
    this.routeDuration = this.dialogData.data?.routeDuration || 1;
    this.routeDetails = this.dialogData.data?.routeDetails;
    this.userDetails = this.dialogData.data?.userDetails;
    this.customUserDetails = this.dialogData.data?.customUserDetails;
    this.showRescheduleSuccessToast = this.dialogData.data?.showRescheduleSuccessToast ?? true;
    this.dialogTitle = this.getDialogTitle();
    this.initDependencies();
  }

  ngOnDestroy(): void {
    this.startDateSubscription.unsubscribe();
    this.startTimePickerSubscription.unsubscribe();
    this.endTimePickerSubscription.unsubscribe();
  }

  initDependencies(): void {
    const hourDifference = this.endDateTime.diff(this.startDateTime, 'minute');
    this.rescheduleRouteForm = this.formBuilder.group({
      startDate: [this.startDateTime, Validators.required],
      startTime: [this.timeFormatPipe.transform(this.startDateTime), Validators.required],
      startTimePicker: [this.startDateTime],
      endDate: [this.endDateTime, Validators.required],
      endTime: [this.timeFormatPipe.transform(this.endDateTime), [Validators.required, , endTimeMinValidator(true)]],
      endTimePicker: [this.endDateTime],
    });

    this.startDateSubscription = this.rescheduleRouteForm.controls.startDate.valueChanges
      .subscribe((startDate: any) => {
        const startTime = this.rescheduleRouteForm.controls.startTimePicker.value;
        const startDateTime = moment(startDate.format('YYYY-MM-DD') + ' ' + startTime.format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
        const endDateTime = startDateTime.add(hourDifference, 'minute');
        if (!this.rescheduleRouteForm.controls.endDate.dirty) {
          this.rescheduleRouteForm.controls.endDate.setValue(endDateTime);
        }
      });

    this.startTimePickerSubscription = this.rescheduleRouteForm.controls.startTimePicker.valueChanges
      .subscribe((startTime: any) => {
        this.rescheduleRouteForm.controls.startTime.setValue(startTime.format(this.timeFormat));
        const startDate = this.rescheduleRouteForm.controls.startDate.value;
        const startDateTime = moment(startDate.format('YYYY-MM-DD') + ' ' + startTime.format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
        this.rescheduleRouteForm.controls.startDate.setValue(startDateTime);
        const endDateTime = startDateTime.clone().add(hourDifference, 'minute');
        if (!this.rescheduleRouteForm.controls.endDate.dirty) {
          this.rescheduleRouteForm.controls.endDate.setValue(endDateTime);
        }
        this.rescheduleRouteForm.controls.endTimePicker.setValue(endDateTime);
      });

    this.endTimePickerSubscription = this.rescheduleRouteForm.controls.endTimePicker.valueChanges
      .subscribe((endTime: any) => {
        this.rescheduleRouteForm.controls.endTime.setValue(endTime.format(this.timeFormat));
        const endDate = this.rescheduleRouteForm.controls.endDate.value;
        const endDateTime = moment(endDate.format('YYYY-MM-DD') + ' ' + endTime.format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
        this.rescheduleRouteForm.controls.endDate.setValue(endDateTime);
        this.getWarningMsg();
      });
    this.getWarningMsg();
    this.changeDetectorRef.markForCheck();
  }

  onDateChange(): void {
    this.getWarningMsg();
    this.rescheduleRouteForm.get('endTime').markAsTouched();
    this.rescheduleRouteForm.get('endTime').updateValueAndValidity();
  }

  isMeridian(): boolean {
    return this.timeFormat === 'hh:mm A';
  };

  rescheduleRoute(): void {
    if(!this.rescheduleRouteForm.invalid) {
      const [routeStartTime, routeEndTime]: Moment[] = this.getStartAndEndTime(true);
      const routeUid = this.routeDetails?.route_uid;
      const duration = this._dispatchBoardService.getTimeDiff(routeStartTime, routeEndTime, 'hour');
      const userUid = this.userDetails?.user_uid;
      const routeStartDateTime = routeStartTime.clone().utc().format('YYYY-MM-DD HH:mm:ss');
      const routeEndDateTime = routeStartTime.clone().utc().add(duration, 'h').format('YYYY-MM-DD HH:mm:ss');
      const updateRouteParam = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        departure: routeStartDateTime,
        duration: duration
      };

      //Check for job overlaps
      let assignedTo: any[] = this.routeDetails?.assigned_to?.map((assignee: any) => _.get(assignee, ['user', 'user_uid']));
      assignedTo.push(userUid);
      assignedTo = _.uniq(assignedTo);
      this.validatingOverlapsAndTimeoff = true;
      const overlapPayload = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        from_date: routeStartDateTime, to_date: routeEndDateTime, route_uid : routeUid, user_uid: assignedTo.join(','), consider_route_overlap: true
      };
      this._dataCollectorService.putData(this._dispatchBoardService.scheduleOverlapUrl, overlapPayload)
        .subscribe(async (response: any) => {
          const customUserDetails = this._dispatchBoardService.getCustomEmployeeDetailsRemote(response?.data);
          const [startDateTime, endDateTime] = this.getStartAndEndTime();
          const warnings = this._dispatchBoardService.getScheduleWarningMsg('route', this.routeDetails, this.userDetails, startDateTime, endDateTime, customUserDetails, false, true);
          const openConfirmationDialog = Object.values(warnings).filter((warning: any) => warning.show).length > 0;
          let confirmationDialogInstance: any;
          if (openConfirmationDialog) {
            confirmationDialogInstance = this._dispatchBoardService.scheduleConfirmationDialog('route',this.routeDetails, warnings);
            this.validatingOverlapsAndTimeoff = false;
            confirmationDialogInstance?.afterClosed()
              .subscribe(async (result: boolean) => {
                if (result) {
                  this.validatingOverlapsAndTimeoff = true;
                  this._dispatchBoardService.updateRoute(routeUid, updateRouteParam, this.showRescheduleSuccessToast, (status: boolean) => {
                    if(status) {
                      this.validatingOverlapsAndTimeoff = false;
                      const dialogData = {data: updateRouteParam};
                      this.dialogRef.close(dialogData);
                    }
                    else {
                      this.dialogRef.close();
                      this.validatingOverlapsAndTimeoff = false;
                    }
                  });
                }
                this.changeDetectorRef.markForCheck();
              });
          }
          else {
            this._dispatchBoardService.updateRoute(routeUid, updateRouteParam, this.showRescheduleSuccessToast, (status: boolean) => {
              if(status) {
                this.validatingOverlapsAndTimeoff = false;
                const dialogData = {data: updateRouteParam};
                this.dialogRef.close(dialogData);
              }
              else {
                this.dialogRef.close();
                this.validatingOverlapsAndTimeoff = false;
              }
            });
          }
          this.changeDetectorRef.markForCheck();
        }, () => {
          this.validatingOverlapsAndTimeoff = false;
      });
    }
  }

  getDialogTitle(): string {
    const routeName = this.routeDetails?.route_name;
    return `${this._utilityService.getLocaleValue('dispatchBoard.scheduler.reschedule.dialog.reschedule_route', 'Reschedule Route')} '${routeName}'`;
  }

  getStartAndEndTime(applyTimezone: boolean = false): any[] {
    const startDate = this.rescheduleRouteForm.controls.startDate.value;
    const startTime = this.rescheduleRouteForm.controls.startTimePicker.value;
    const endDate = this.rescheduleRouteForm.controls.endDate.value;
    const endTime = this.rescheduleRouteForm.controls.endTimePicker.value;

    let startDateTime: any;
    let endDateTime: any;

    if (applyTimezone) {
      startDateTime = moment.tz(startDate.format('YYYY-MM-DD') + ' ' + startTime.format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss', this._dispatchBoardService.selectedTimezone[this._dispatchBoardService.activeDispatchTab]).utc(false);
      endDateTime = moment.tz(endDate.format('YYYY-MM-DD') + ' ' + endTime.format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss', this._dispatchBoardService.selectedTimezone[this._dispatchBoardService.activeDispatchTab]).utc(false);
    }
    else {
      startDateTime = moment(startDate.format('YYYY-MM-DD') + ' ' + startTime.format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
      endDateTime = moment(endDate.format('YYYY-MM-DD') + ' ' + endTime.format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');
    }
    return [startDateTime, endDateTime];
  }
}
